import React from 'react'
import { AxiosError } from 'axios'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'
import { routeTree } from '@/routeTree.gen'
import { NuqsAdapter } from 'nuqs/adapters/react'
import { Toaster } from 'sonner'
import { handleServerError } from '@/utils/handle-server-error'
import { AuthProvider } from '@/context/auth-context'
import JobUploadContext from '@/context/job-upload'
import { OrgProvider } from '@/context/org-context'
import ResDBContext from '@/context/resdb-upload'
import { SubscriptionProvider } from '@/context/subscription-context'
import { ThemeProvider } from '@/context/theme-context'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AppErrorBoundary } from '@/pages/errors/error-boundary'
import ConfirmDialogProvider from './conform-dialog'

declare module '@tanstack/react-router' {
  interface HistoryState {
    fromCommandMenu?: boolean
    title?: string
    description?: string
    from?:
    | string
    | {
      pathname: string
    }
    open_supademo?: true
    draftId?: string
    open_sync?: boolean
    access_token?: string
    access_denied?: boolean
    pathname?: string
    invites?: boolean
    templateId?: string | null
    openActivity?: boolean
    activityId?: string | null
    job?: {
      posting_success?: boolean
      title: string
    }
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,    // Don't re-fetch when switching tabs
      refetchOnReconnect: false,      // Don't re-fetch on reconnect
      refetchOnMount: false,          // Don't re-fetch when component re-mounts if cache is fresh

      // ── Industry-standard: 5-minute stale time (Linear, Vercel, Notion pattern) ──
      // With 60s staleTime, every page navigation fired a new API call because
      // React Query considered data stale immediately after 1 minute.
      // 5 minutes eliminates the vast majority of redundant requests.
      staleTime: 5 * 60 * 1000,       // Data is "fresh" for 5 minutes
      gcTime: 10 * 60 * 1000,         // Keep unused cache for 10 minutes (garbage collection)

      retry: 1,                       // Retry failed requests only once
      retryDelay: 1000,               // Wait 1s before retry
    },
    mutations: {
      onError: (error) => {
        handleServerError(error)
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof AxiosError) {
        if (error.response?.status === 500) {
          console.error('[API] Internal Server Error (500)')
        }
        if (error.response?.status === 403) {
          router.navigate({ to: '/403', replace: true })
        }
      }
    },
  }),
})

export const router = createRouter({
  routeTree,
  context: { queryClient },
  // ── Intent-based preloading (TanStack Router) ──
  // When a user hovers over a link (e.g., "Jobs" in the sidebar), TanStack Router
  // prefetches that route's loaderData. Combined with 5-min staleTime, this means
  // the jobs page is often already loaded by the time the user clicks.
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 30_000,  // Preloaded data is valid for 30s
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export default function Index({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider>
      <NuqsAdapter>
        <ResDBContext>
          <ConfirmDialogProvider>
            <QueryClientProvider client={queryClient}>
              <JobUploadContext>
                <TooltipProvider>
                  {/* AuthProvider is placed above Org and Subscription to allow child contexts to load correctly */}
                  <AuthProvider>
                    <OrgProvider>
                      <SubscriptionProvider>
                        <AppErrorBoundary>
                          <Toaster richColors />
                          {children}
                        </AppErrorBoundary>
                      </SubscriptionProvider>
                    </OrgProvider>
                  </AuthProvider>
                </TooltipProvider>
              </JobUploadContext>
            </QueryClientProvider>
          </ConfirmDialogProvider>
        </ResDBContext>
      </NuqsAdapter>
    </ThemeProvider>
  )
}
